import React, {FC, useState, useEffect} from 'react'
import {showSwal} from '../../../../ui-kit'
import {useParams, useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {TradeProvision} from '../trade-provisions/core/_models'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {
  getTradeProvisionById,
  createTradeProvision,
  updateTradeProvisionById,
} from '../trade-provisions/core/_requests'
import TradeProvisionsDetailView from './components/TradeProvisionsDetailView'
import SummernoteEditor from '../../../../ui-kit/SummerNote/SummerNote'
import {TagInput} from './components/TagInput'

const TradeProvisionsDetailPage: FC<{subject: string}> = ({subject}) => {
  const {idTradeProvision} = useParams<{idTradeProvision: string}>()
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const ability = useAbility(AbilityContext)

  const isAddMode = !idTradeProvision
  const isDetailMode = !!idTradeProvision
  const [isEditMode, setIsEditMode] = useState(isAddMode)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)

  const [tradeProvisionDetail, setTradeProvisionDetail] = useState<TradeProvision>({
    provision: '',
    legally_enforceable: null,
    description: '',
    keyword_combinations: '',
  })

  useEffect(() => {
    const loadDetail = async () => {
      if (!isDetailMode || !idTradeProvision) return
      setIsLoading(true)
      try {
        const detailRes = await getTradeProvisionById(axiosPrivate, idTradeProvision)
        const detailData = detailRes.data.data
        if (detailData) {
          setTradeProvisionDetail({
            provision: detailData.provision || '',
            legally_enforceable: detailData.legally_enforceable ?? null,
            description: detailData.description || '',
            keyword_combinations: detailData.keyword_combinations || '',
          })
        }
      } catch (err) {
        console.error('Failed to load trade provision detail:', err)
        showSwal('Error', 'Failed to load trade provision details.', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadDetail()
  }, [idTradeProvision])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setTradeProvisionDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setTradeProvisionDetail((prev) => ({
      ...prev,
      legally_enforceable: value === '' ? null : Number(value),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!tradeProvisionDetail.provision.trim()) {
      showSwal('Warning', 'Provision name is required.', 'warning')
      return
    }

    showSwal({
      title: `Are you sure you want to ${isAddMode ? 'create' : 'update'} this trade provision?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)
      const payload = {
        provision: tradeProvisionDetail.provision.trim(),
        legally_enforceable: tradeProvisionDetail.legally_enforceable,
        description: tradeProvisionDetail.description?.trim() || null,
        keyword_combinations: tradeProvisionDetail.keyword_combinations?.trim() || null,
      }

      if (isAddMode) {
        createTradeProvision(axiosPrivate, payload)
          .then((res) => {
            if (res.status === 201) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/trade-provisions')
              })
            }
          })
          .catch((err) => {
            console.error('Failed to create trade provision:', err)
            showSwal('Error', 'Failed to create trade provision.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        updateTradeProvisionById(axiosPrivate, idTradeProvision, payload)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error('Failed to update trade provision:', err)
            showSwal('Error', 'Failed to update trade provision.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/trade-provisions')
        } else {
          setIsEditMode(false)
          window.location.reload()
        }
      }
    })
  }

  const isFormValid = !!tradeProvisionDetail.provision.trim()

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Provisions',
            path: '/trade-provisions',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode
          ? 'Add Trade Provision'
          : isEditMode
          ? `Edit Trade Provision: ${tradeProvisionDetail.provision}`
          : `Trade Provision Detail`}
      </PageTitle>

      <div className='TradeProvisionsDetailPage'>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-handshake px-2'></span>
                      <h3 className='text-gray-700'>
                        {isAddMode
                          ? 'Add New Trade Provision'
                          : isEditMode
                          ? 'Edit Trade Provision Details'
                          : 'Trade Provision Detail'}
                      </h3>
                    </div>
                    {ability.can('update', subject) && isDetailMode && !isEditMode && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoading ? (
                  isEditMode ? (
                    <Form onSubmit={handleSubmit} className='form-trade-provisions'>
                      <div className='row mb-4'>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Provision <span className='text-danger'>*</span>
                          </Form.Label>
                          <Form.Control
                            placeholder='Enter provision name'
                            type='text'
                            name='provision'
                            value={tradeProvisionDetail.provision}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Legally Enforceable
                          </Form.Label>
                          <Form.Select
                            name='legally_enforceable'
                            value={tradeProvisionDetail.legally_enforceable ?? ''}
                            onChange={handleSelectChange}
                          >
                            <option value=''>Not Specified</option>
                            <option value='1'>Yes</option>
                            <option value='0'>No</option>
                          </Form.Select>
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>Description</Form.Label>
                          <SummernoteEditor
                            value={tradeProvisionDetail.description || ''}
                            onChange={(html) => {
                              const fakeEvent = {
                                target: {
                                  name: 'description',
                                  value: html,
                                },
                              } as any
                              handleInputChange(fakeEvent)
                            }}
                          />
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Keyword Combinations
                          </Form.Label>
                          <TagInput
                            placeholder='Enter keyword combinations and press Enter'
                            value={tradeProvisionDetail.keyword_combinations || ''}
                            onChange={(newValue) => {
                              setTradeProvisionDetail((prev) => ({
                                ...prev,
                                keyword_combinations: newValue,
                              }))
                            }}
                          />
                        </div>
                      </div>

                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            type='submit'
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <TradeProvisionsDetailView tradeProvisionDetail={tradeProvisionDetail} />
                  )
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default TradeProvisionsDetailPage
